import { Box, FormField, Spinner, Text, TextInput, Tip } from 'grommet';
import { WidthType } from 'grommet/utils';
import React, { KeyboardEventHandler, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { HiOutlineExclamationTriangle } from 'react-icons/hi2';

import { InlineEdit } from '@/components/form-controls';
import { BidRecommendation, BidRecommendationDrugPackaging } from '@/features/bid-analysis/types/bid-recommendation';

import * as BidRecommendationFunctions from './bid-recommendation.functions';
import { BidRecommendationPackagingFormInputs } from './bid-recommendation-packaging-form-inputs';

const PBM_CHARACTER_LIMIT = 220;

type TextInlineEditCellProps = {
  fieldName: keyof BidRecommendationPackagingFormInputs,
  bidRecommendation: BidRecommendation,
  drugPackaging: BidRecommendationDrugPackaging,
  editingFieldId?: string;
  savingFieldId?: string;
  onFocus: (fieldId: string) => void;
  onCancel: () => void;
  onSave: (
    bidRecommendation: BidRecommendation,
    row: BidRecommendationDrugPackaging,
    fieldId: string,
    form: BidRecommendationPackagingFormInputs) => void;
  cellWidth?: WidthType
};

export const TextInlineEditCell = (props: TextInlineEditCellProps) => {
  const {
    fieldName,
    bidRecommendation,
    drugPackaging,
    editingFieldId,
    savingFieldId,
    onFocus,
    onCancel,
    onSave,
    cellWidth = 'large'
  } = props;

  const id = BidRecommendationFunctions.buildFieldId({
    bidRecommendationId: bidRecommendation.id,
    isHistorical: bidRecommendation.isHistorical,
    packagingId: drugPackaging.id,
    fieldName
  });

  const editing = editingFieldId === id;
  const saving = savingFieldId === id;

  const {
    control,
    getValues,
    watch,
    formState: { errors, isDirty  },
    reset
  } = useForm<BidRecommendationPackagingFormInputs>({ mode: 'onChange', reValidateMode: 'onChange', defaultValues: { [fieldName]: drugPackaging[fieldName as keyof BidRecommendationDrugPackaging] } });

  useEffect(() => {
    reset({ [fieldName]: drugPackaging[fieldName as keyof BidRecommendationDrugPackaging] });
  }, [drugPackaging[fieldName as keyof BidRecommendationDrugPackaging]]);

  const handleFocus = () => {
    onFocus(id);
  };

  const handleSave = async (form: BidRecommendationPackagingFormInputs) => {
    if (isDirty) {
      onSave(bidRecommendation, drugPackaging, id, { ...form });
    } else {
      onCancel();
      reset();
    }
  };

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter') {
      handleSave(getValues());
    }

    if (event.key === 'Escape') {
      reset();
      onCancel();
    }
  };

  const currentValue = watch(fieldName) as string | undefined;
  const isOverPbmLimit = (currentValue?.length ?? 0) >= PBM_CHARACTER_LIMIT;

  return (
    <Box width={cellWidth} fill="vertical">
      <Controller
        control={control}
        name={fieldName}
        rules={{
          required: true
        }}
        render={({ field: { value, onChange } }) => (
          <InlineEdit
            editing={editing}
            onFocus={handleFocus}
            readonlyComponent={
              <Box fill pad="xxsmall" direction="row" align="center" gap="xsmall">
                <span>{value ?? <>&mdash;</>}</span>
                {(value?.toString().length ?? 0) >= PBM_CHARACTER_LIMIT && (
                  <Tip content={`Exceeds 220 character limit for PBM bid forms (currently ${value?.toString().length} characters).`}>
                    <Box flex={false}>
                      <HiOutlineExclamationTriangle color="#FFD48A" size="16px" />
                    </Box>
                  </Tip>
                )}
              </Box>
            }
            inputComponent={
              <Box>
                <FormField
                  name={fieldName}
                  htmlFor={id}
                  error={errors.supplementalRebate?.message}
                >
                  <TextInput
                    id={id}
                    value={value}
                    onChange={onChange}
                    onBlur={() => handleSave(getValues())}
                    onKeyDown={handleKeyDown}
                    name={fieldName}
                    icon={saving ? <Spinner size="xsmall" color="brand"/> : undefined}
                    reverse
                  />
                </FormField>
                {isOverPbmLimit && (
                  <Box
                    background="status-warning"
                    round="xxsmall"
                    pad={{ horizontal: 'xsmall', vertical: '2px' }}
                    margin={{ top: '2px' }}
                    direction="row"
                    align="start"
                    gap="xxsmall"
                    flex={false}
                  >
                    <Box flex={false} pad={{ top: '1px' }}>
                      <HiOutlineExclamationTriangle size="14px"/>
                    </Box>
                    <Text size="xsmall">
                      {currentValue?.length}/{PBM_CHARACTER_LIMIT} — Exceeds 220 character limit for PBM bid forms
                    </Text>
                  </Box>
                )}
              </Box>
            }
          />
        )}
      />
    </Box>
  );
};
